import React from 'react';
import { useTranslation } from 'react-i18next';

import { WidgetSlot, WidgetType } from '@/api/rest/resources/types/widget';
import { ChartShape } from '@/components/widgets/o/CompositeGraph/CompositeGraph';
// eslint-disable-next-line import/no-cycle
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { useActiveWidgetSlotConfiguration } from '@/hooks/useWidgetConfigurationByOrganizationId';
import { isNullish } from '@/utils/booleans';

import { useOrganizationFinancialImpact } from '../hooks/useOrganizationFinancialImpact';

const C = {
  base: '#909090',
  penalty: '#D4957C',
  baseline: '#CCC08A',
  intervention: '#7EC8C4',
} as const;

const formatMoney = (value: number) => {
  const abs = Math.abs(value);
  const sign = value >= 0 ? '+' : '-';
  return `${sign}$${abs.toFixed(1)}M`;
};

const formatMoneyUnsigned = (value: number) => `$${Math.abs(value).toFixed(1)} M`;

const formatPct = (value: number) => `${(value * 100).toFixed(1)}%`;

const renderValue = (value: string): React.ReactNode => {
  if (typeof value === 'string' && value.startsWith('+'))
    return <span className='font-semibold text-success-dark'>{value}</span>;
  if (typeof value === 'string' && (value.startsWith('−') || value.startsWith('-')))
    return <span className='font-semibold text-error'>{value}</span>;
  return <span>{value}</span>;
};

const COMPARISON_COLUMNS = [
  { key: 'metric', label: '', className: 'text-text-primary w-1/3' },
  {
    key: 'baseline',
    label: 'BASELINE',
    // eslint-disable-next-line sonarjs/no-duplicate-string
    className: 'text-right',
    render: renderValue,
  },
  {
    key: 'recommended',
    label: 'RECOMMENDED',
    className: 'text-right',
    render: renderValue,
  },
  {
    key: 'highAmbition',
    label: 'HIGH AMBITION',
    className: 'text-right',
    render: renderValue,
  },
];

// eslint-disable-next-line sonarjs/cognitive-complexity
export const ScenarioFinancialDetails = () => {
  const { t } = useTranslation();
  const { data } = useOrganizationFinancialImpact();

  const HEADERS_CLASSNAME = 'flex flex-col gap-2';
  const HEADER_CLASSNAME = 'typography-overline text-text-primary font-semibold';
  const SUBHEADER_CLASSNAME = 'typography-body text-text-secondary';
  const DESCRIPTION_CLASSNAME = 'text-text-secondary';
  const TITLE_CLASSNAME = 'text-text-primary';

  type VS = (typeof data.valuation_states)[number];
  const states = data.valuation_states as [VS, VS, VS, VS];
  const [state1, state2, state3, state4] = states;

  const VALUATION_STATE_CLASSNAME = {
    root: 'bg-neutral-black-4',
    label: DESCRIPTION_CLASSNAME,
    value: DESCRIPTION_CLASSNAME,
  };
  const NRP_VALUATIONS = [
    {
      name: 'pre_nrp_value',
      title: 'Given value',
      value: formatMoneyUnsigned(state1.equity_value),
      tooltip: 'Without accounting for nature risks',
      styles: { ...VALUATION_STATE_CLASSNAME, delta: 'text-error' },
      delta: formatMoney(state2.delta_from_prior),
    },
    {
      name: 'opacity_penalty',
      title: 'Counterfactual value',
      value: formatMoneyUnsigned(state2.equity_value),
      tooltip:
        'When the enterprise does not undertake any scientifically meaningful ways to measure their risk exposure and is therefore penalized by the maximum opacity penalty projected for their sector',
      styles: { ...VALUATION_STATE_CLASSNAME, delta: 'text-warning' },
      delta: formatMoney(state3.delta_from_prior),
    },
    {
      name: 'baseline_nrp_value',
      title: 'Measured value',
      value: formatMoneyUnsigned(state3.equity_value),
      tooltip:
        "When measured with Landler's methodology, giving the customer a transparency benefit that improves their valuation",
      styles: { ...VALUATION_STATE_CLASSNAME, delta: 'text-success' },
      delta: formatMoney(state4.delta_from_prior),
    },
    {
      name: 'post_intervention_value',
      title: 'Post-intervention value',
      value: formatMoneyUnsigned(state4.equity_value),
      tooltip:
        "When one or more of Landler's proposed interventions are implemented, giving the customer an intervention benefit to further improve their financial position",
      styles: { root: TITLE_CLASSNAME, label: TITLE_CLASSNAME, value: TITLE_CLASSNAME, delta: '' },
      delta: undefined,
    },
  ];

  const opacityPenalty = 'Opacity penalty';
  const transparencyBenefit = 'Transparency benefit';
  const netInterventionBenefit = 'Net intervention benefit';
  const opacityBridge = data.bridge.find((b) => b.item === opacityPenalty);
  const transparencyBridge = data.bridge.find((b) => b.item === transparencyBenefit);
  const netBenefitBridge = data.bridge.find((b) => b.item === netInterventionBenefit);

  const NRP_VALUATION_DELTAS = [
    {
      name: 'opacity_penalty',
      title: opacityBridge?.item ?? opacityPenalty,
      value: formatMoney(opacityBridge?.amount ?? 0),
      tooltip: opacityBridge?.interpretation ?? '',
      styles: { root: 'text-error', title: DESCRIPTION_CLASSNAME },
    },
    {
      name: 'baseline_nrp_value',
      title: transparencyBridge?.item ?? transparencyBenefit,
      value: formatMoney(transparencyBridge?.amount ?? 0),
      tooltip: transparencyBridge?.interpretation ?? '',
      styles: { root: 'text-warning', title: DESCRIPTION_CLASSNAME },
    },
    {
      name: 'post_intervention_value',
      title: netBenefitBridge?.item ?? netInterventionBenefit,
      value: formatMoney(netBenefitBridge?.amount ?? 0),
      tooltip: netBenefitBridge?.interpretation ?? '',
      styles: { root: 'text-success bg-white-100', title: TITLE_CLASSNAME },
    },
  ];

  const stateToLabel = {
    'State 1': 'Given value',
    'State 2': 'Counterfactual value',
    'State 3': 'Measured value',
    'State 4': 'Post-intervention value',
  } as const;
  const WACC_DATA = data.rate_decomposition.map((row) => ({
    label: stateToLabel[row.state as keyof typeof stateToLabel] ?? row.state,
    value: Math.round(row.wacc * 1000) / 10,
  }));

  const sc = data.scenario_comparison;
  const baseline = sc.scenarios.Baseline;
  const recommended = sc.scenarios.Recommended;
  const highAmbition = sc.scenarios['High ambition'];
  const COMPARISON_DATA = [
    {
      metric: 'NRP — pre-intervention (measured)',
      baseline: formatPct(sc.nrp_pre_intervention),
      recommended: formatPct(sc.nrp_pre_intervention),
      highAmbition: formatPct(sc.nrp_pre_intervention),
    },
    {
      metric: 'NRP — post-intervention',
      baseline: formatPct(baseline?.nrp_post_y10 ?? 0),
      recommended: formatPct(recommended?.nrp_post_y10 ?? 0),
      highAmbition: formatPct(highAmbition?.nrp_post_y10 ?? 0),
    },
    {
      metric: 'Adjusted discount rate (WACC)',
      baseline: formatPct(baseline?.wacc_y10 ?? 0),
      recommended: formatPct(recommended?.wacc_y10 ?? 0),
      highAmbition: formatPct(highAmbition?.wacc_y10 ?? 0),
    },
    {
      metric: 'Pre intervention Enterprise value · NPV',
      baseline: formatMoneyUnsigned(state1.enterprise_value ?? 0),
      recommended: formatMoneyUnsigned(state1.enterprise_value ?? 0),
      highAmbition: formatMoneyUnsigned(state1.enterprise_value ?? 0),
    },
    {
      metric: opacityPenalty,
      baseline: formatMoney(sc.opacity_penalty),
      recommended: formatMoney(sc.opacity_penalty),
      highAmbition: formatMoney(sc.opacity_penalty),
    },
    {
      metric: transparencyBenefit,
      baseline: formatMoney(sc.transparency_benefit),
      recommended: formatMoney(sc.transparency_benefit),
      highAmbition: formatMoney(sc.transparency_benefit),
    },
    {
      metric: 'Intervention benefit (gross)',
      baseline: formatMoney(baseline?.gross_benefit ?? 0),
      recommended: formatMoney(recommended?.gross_benefit ?? 0),
      highAmbition: formatMoney(highAmbition?.gross_benefit ?? 0),
    },
    {
      metric: 'Intervention cost',
      baseline: formatMoney(-(baseline?.nominal_cost ?? 0)),
      recommended: formatMoney(-(recommended?.nominal_cost ?? 0)),
      highAmbition: formatMoney(-(highAmbition?.nominal_cost ?? 0)),
    },
    {
      metric: netInterventionBenefit,
      baseline: formatMoney(baseline?.net_benefit ?? 0),
      recommended: formatMoney(recommended?.net_benefit ?? 0),
      highAmbition: formatMoney(highAmbition?.net_benefit ?? 0),
    },
    {
      metric: 'Post intervention Enterprise value · NPV',
      baseline: formatMoneyUnsigned(baseline?.ev ?? 0),
      recommended: formatMoneyUnsigned(recommended?.ev ?? 0),
      highAmbition: formatMoneyUnsigned(highAmbition?.ev ?? 0),
    },
  ];

  const valuationStateWidget: WidgetSlot[] = [
    {
      name: 'valuation_state_widgets',
      type: WidgetType.a_stack,
      visible: true,
      relatedData: { className: HEADERS_CLASSNAME },
      widgets: [
        {
          name: 'financial_impact_state_flow_section_headers',
          type: WidgetType.a_stack,
          visible: true,
          relatedData: { className: HEADERS_CLASSNAME },
          widgets: [
            {
              name: 'financial_impact_state_flow_section_header',
              type: WidgetType.a_text,
              visible: true,
              relatedData: {
                text: t('shared.scenarioAnalysis.labels.valuationStates'),
                className: HEADER_CLASSNAME,
              },
            },
            {
              name: 'financial_impact_state_flow_section_header',
              type: WidgetType.a_text,
              visible: true,
              relatedData: {
                text: t('shared.scenarioAnalysis.labels.valuationStatesDescription'),
                className: SUBHEADER_CLASSNAME,
              },
            },
          ],
        },

        {
          name: 'financial_impact_state_flow_section',
          type: WidgetType.a_stack,
          visible: true,
          relatedData: { className: 'flex flex-col gap-6 px-0' },
          widgets: [
            {
              name: 'financial_impact_cards',
              type: WidgetType.a_stack,
              visible: true,
              relatedData: { className: 'flex flex-row gap-3 justify-center items-center' },
              widgets: NRP_VALUATIONS.flatMap((valuation): WidgetSlot[] => [
                {
                  name: `financial_impact_${valuation.name}`,
                  type: WidgetType.o_card,
                  visible: true,
                  relatedData: {
                    styles: {
                      ...valuation.styles,
                      root: `lg:basis-1/4 rounded-lg ${valuation.styles.root}`,
                      body: 'flex flex-col gap-4 p-3 justify-center items-center',
                    },
                  },
                  widgets: [
                    {
                      name: `financial_impact_${valuation.name}_header`,
                      type: WidgetType.a_text,
                      visible: true,
                      widgets: [
                        {
                          name: `financial_impact_${valuation.name}_label`,
                          type: WidgetType.a_text,
                          visible: true,
                          relatedData: {
                            text: valuation.title,
                            className: `typography-body1 text-text-primary ${valuation.styles.label}`,
                          },
                        },
                        {
                          name: `financial_impact_${valuation.name}_tooltip`,
                          type: WidgetType.m_tooltip,
                          visible: true,
                          relatedData: {},
                          widgets: [
                            {
                              name: `financial_impact_${valuation.name}_tooltip_trigger`,
                              type: WidgetType.m_tooltip_trigger,
                              visible: true,
                              widgets: [
                                {
                                  name: `financial_impact_${valuation.name}_tooltip_trigger_icon`,
                                  type: WidgetType.a_icon,
                                  visible: true,
                                  relatedData: {
                                    icon: 'ri-information-line',
                                    className: 'ml-1 text-active-54',
                                  },
                                },
                              ],
                            },
                            {
                              name: `financial_impact_${valuation.name}_tooltip_content`,
                              type: WidgetType.m_tooltip_content,
                              visible: true,
                              widgets: [
                                {
                                  name: `financial_impact_${valuation.name}_tooltip_text`,
                                  type: WidgetType.a_text,
                                  visible: true,
                                  relatedData: {
                                    text: valuation.tooltip,
                                    className: 'whitespace-pre-wrap',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      name: `financial_impact_${valuation.name}_value`,
                      type: WidgetType.a_text,
                      visible: true,
                      relatedData: {
                        text: valuation.value,
                        className: `typography-h2 text-text-primary ${valuation.styles.value}`,
                      },
                    },
                  ],
                },
                {
                  name: `financial_impact_${valuation.name}_delta`,
                  type: WidgetType.a_stack,
                  visible: !isNullish(valuation.delta),
                  relatedData: {
                    className: `typography-body2 w-fit justify-center items-center gap-0.5 ${valuation.styles.delta}`,
                  },
                  widgets: [
                    {
                      name: `financial_impact_${valuation.name}_delta_icon`,
                      type: WidgetType.a_icon,
                      visible: !isNullish(valuation.delta),
                      relatedData: {
                        icon: 'ri-arrow-right-long-line',
                        className: 'typography-h3',
                      },
                    },
                    {
                      name: `financial_impact_${valuation.name}_delta_value`,
                      type: WidgetType.a_text,
                      visible: !isNullish(valuation.delta),
                      relatedData: {
                        text: valuation.delta,
                      },
                    },
                  ],
                },
              ]),
            },
            {
              name: 'financial_impact_delta_cards',
              type: WidgetType.a_stack,
              visible: true,
              relatedData: { className: 'grid grid-cols-3 gap-3' },
              widgets: NRP_VALUATION_DELTAS.map(
                (valuation): WidgetSlot => ({
                  name: `financial_impact_delta_${valuation.name}`,
                  type: WidgetType.o_card,
                  visible: true,
                  relatedData: {
                    styles: {
                      ...valuation.styles,
                      root: `lg:basis-1/4 rounded-lg bg-neutral-black-4 ${valuation.styles.root}`,
                      body: `flex flex-col gap-4 p-3 justify-center items-center`,
                    },
                  },
                  widgets: [
                    {
                      name: `financial_impact_delta_${valuation.name}_header`,
                      type: WidgetType.a_text,
                      visible: true,
                      widgets: [
                        {
                          name: `financial_impact_delta_${valuation.name}_label`,
                          type: WidgetType.a_text,
                          visible: true,
                          relatedData: {
                            text: valuation.title,
                            className: `typography-body1 ${valuation.styles.title}`,
                          },
                        },
                        {
                          name: `financial_impact_delta_${valuation.name}_tooltip`,
                          type: WidgetType.m_tooltip,
                          visible: true,
                          widgets: [
                            {
                              name: `financial_impact_delta_${valuation.name}_tooltip_trigger`,
                              type: WidgetType.m_tooltip_trigger,
                              visible: true,
                              widgets: [
                                {
                                  name: `financial_impact_delta_${valuation.name}_tooltip_trigger_icon`,
                                  type: WidgetType.a_icon,
                                  visible: true,
                                  relatedData: {
                                    icon: 'ri-information-line',
                                    className: 'ml-1 text-active-54',
                                  },
                                },
                              ],
                            },
                            {
                              name: `financial_impact_delta_${valuation.name}_tooltip_content`,
                              type: WidgetType.m_tooltip_content,
                              visible: true,
                              widgets: [
                                {
                                  name: `financial_impact_delta_${valuation.name}_tooltip_text`,
                                  type: WidgetType.a_text,
                                  visible: true,
                                  relatedData: {
                                    text: valuation.tooltip,
                                    className: 'whitespace-pre-wrap typography-body2 text-text-primary',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      name: `financial_impact_delta_${valuation.name}_value`,
                      type: WidgetType.a_text,
                      visible: true,
                      relatedData: {
                        text: valuation.value,
                        className: 'typography-h2',
                      },
                    },
                  ],
                }),
              ),
            },
          ],
        },
      ],
    },
  ];

  const waccChartWidget: WidgetSlot[] = [
    {
      name: 'financial_impact_wacc_chart_widgets',
      type: WidgetType.a_stack,
      visible: true,
      relatedData: { className: HEADERS_CLASSNAME },
      widgets: [
        {
          name: 'financial_impact_wacc_chart_headers',
          type: WidgetType.a_stack,
          visible: true,
          relatedData: { className: HEADERS_CLASSNAME },
          widgets: [
            {
              name: 'financial_impact_wacc_chart_header',
              type: WidgetType.a_text,
              visible: true,
              relatedData: {
                text: t('shared.scenarioAnalysis.labels.nrpDecomposition'),
                className: HEADER_CLASSNAME,
              },
            },
            {
              name: 'financial_impact_wacc_chart_header',
              type: WidgetType.a_text,
              visible: true,
              relatedData: {
                text: t('shared.scenarioAnalysis.labels.nrpDecompositionDescription'),
                className: SUBHEADER_CLASSNAME,
              },
            },
          ],
        },
        {
          name: 'financial_impact_wacc_chart_card',
          type: WidgetType.o_card,
          visible: true,
          relatedData: {
            styles: {
              root: 'border border-neutral-black-12 rounded-lg overflow-hidden',
              body: 'p-3',
            },
          },
          widgets: [
            {
              name: 'financial_impact_wacc_graph',
              type: WidgetType.o_composite_graph,
              visible: true,
              relatedData: {
                data: WACC_DATA,
                chartConfig: [
                  {
                    shape: ChartShape.bar,
                    dataKey: 'value',
                    label: 'Discount rate',
                    color: C.base,
                    key: 'discount_rate',
                    id: 'discount_rate',
                    barColors: [C.base, C.penalty, C.baseline, C.intervention],
                    barSize: 80,
                    showLabel: true,
                    unit: '%',
                  },
                ],
                xAxisProps: {
                  dataKey: 'label',
                  type: 'category',
                  interval: 0,
                },
              },
            },
          ],
        },
      ],
    },
  ];

  const scenarioComparisonTableWidget: WidgetSlot[] = [
    {
      name: 'financial_impact_comparison_section_widgets',
      type: WidgetType.a_stack,
      visible: true,
      relatedData: { className: HEADERS_CLASSNAME },
      widgets: [
        {
          name: 'financial_impact_comparison_section_headers',
          type: WidgetType.a_stack,
          visible: true,
          relatedData: { className: HEADERS_CLASSNAME },
          widgets: [
            {
              name: 'financial_impact_comparison_section_header',
              type: WidgetType.a_text,
              visible: true,
              relatedData: {
                text: t('shared.scenarioAnalysis.labels.scenarioComparison'),
                className: HEADER_CLASSNAME,
              },
            },
            {
              name: 'financial_impact_comparison_section_header',
              type: WidgetType.a_text,
              visible: true,
              relatedData: {
                text: t('shared.scenarioAnalysis.labels.scenarioComparisonDescription'),
                className: SUBHEADER_CLASSNAME,
              },
            },
          ],
        },
        {
          name: 'financial_impact_comparison_section',
          type: WidgetType.a_stack,
          visible: true,
          relatedData: { className: 'flex flex-col gap-3' },
          widgets: [
            {
              name: 'financial_impact_comparison_table',
              type: WidgetType.o_simple_table,
              visible: true,
              relatedData: {
                columns: COMPARISON_COLUMNS,
                data: COMPARISON_DATA,
                className: 'rounded-lg border border-neutral-black-12 overflow-hidden',
              },
            },
          ],
        },
      ],
    },
  ];

  const defaultMethodologyConfiguration = useActiveWidgetSlotConfiguration(
    'default_scenario_modelling_methodology_inputs_tab',
    WidgetType.a_stack,
  );

  const summaryWidget = (defaultMethodologyConfiguration?.widgets as WidgetSlot[] | undefined)?.find(
    (w: WidgetSlot) => w.name === 'financial_impact_summary_stack',
  ) ?? {
    name: 'financial_impact_summary_stack',
    type: WidgetType.o_card,
    visible: false,
  };

  const defaultConfiguration: WidgetSlot = {
    name: 'financial_impact_tab',
    type: WidgetType.a_stack,
    visible: true,
    relatedData: { className: 'flex flex-col gap-8' },
    widgets: [
      { ...summaryWidget, visible: true },
      ...valuationStateWidget,
      ...waccChartWidget,
      ...scenarioComparisonTableWidget,
    ],
  };
  return <WidgetSlotRenderer nestedWidget={defaultConfiguration} />;
};
